import React from "react";
import { FontIcon, Spinner, SpinnerSize, Text, TooltipDelay, TooltipHost, TooltipOverflowMode } from "@fluentui/react";
import { DragAndDropHelper } from "../../../../../../../utilities/DragAndDropHelper";
import { LanguageContext } from "../../../../../../../utilities/LocalizationModule";
import { dragZoneStyleSets } from "../../../../../config/controlStyles";
import { mobileListItemStyle } from "../../../../../styles/mobileStyles";
import { hasChildren, treeViewItemStyle } from "../../../../helpers/treeHelperGeneral";
import { ISiteInfoLocation } from "../../../../../interfaces/ISiteInfo";
import { ITessAccountLocation } from "../../../../../services/TessService";
import { BrandIcons } from "../../../../../config/brandIcons";
import { ArrowClockwise20Regular } from "../../SvgIcons/ArrowClockwise20Regular";
import { MoreVertical20Regular } from "../../SvgIcons/MoreVertical20Regular";
import { PauseCircle20Regular } from "../../SvgIcons/PauseCircle20Regular";
import { smallPadLeft } from "../../../../../styles/iconStyles";
import { useOfficeDropTarget } from "./useOfficeDropTarget";

interface ITessAccountItemProps {
    item: ITessAccountLocation;
    isMobile: boolean;
    allowScroll: boolean;
    onSiteClick: (item: ISiteInfoLocation) => void;
    onOptionsClick: (item: ISiteInfoLocation) => void;
    onExpandCollapseClick: (item: ITessAccountLocation, isRefresh?: boolean) => Promise<void>
}

export const TessAccountItem = ({ item, isMobile, allowScroll, onSiteClick, onOptionsClick, onExpandCollapseClick }: ITessAccountItemProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const [isDraggedOver, setIsDraggedOver] = React.useState(false);
    const dragCounter = React.useRef<number>(0);
    const rootRef = React.useRef<HTMLDivElement>(null);

    // Hover-only parity for New Outlook / OWA email drags (pane-global Office.js): this
    // tile rejects drops (no onDrop — the pane-global handler swallows drops here, matching
    // the DOM path's dropEffect="none"), but hovering shows the same grey affordance and
    // runs the same hover-expand timer below, via the shared isDraggedOver state.
    useOfficeDropTarget(rootRef, {
        onDragHoverChange: setIsDraggedOver,
        canHover: () => true,
    });

    const dragEnter = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        if (DragAndDropHelper.validateDroppedFiles(event) || DragAndDropHelper.validateDroppedGridItems(event)) {
            event.preventDefault();
            event.stopPropagation();
            event.dataTransfer.dropEffect = "none";
            dragCounter.current = dragCounter.current + 1;
            setIsDraggedOver(true);
        }
    }, []);

    const dragOver = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = "none";
    }, []);

    const dragLeave = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.stopPropagation();
        if (isDraggedOver) {
            dragCounter.current = dragCounter.current - 1;
            if (!dragCounter.current || event.target === event.currentTarget) {
                setIsDraggedOver(false);
            }
        }
    }, [isDraggedOver]);

    const className = React.useMemo((): string => {
        let className: string = "list-item";
        if (item) {
            className += ` ${"tree-item"}`;
            if (item.isSelected) {
                className += ` ${"tree-item-selected"}`;
            }
        }

        if (isDraggedOver) {
            className += ` dropzone ${dragZoneStyleSets.dragzoneActive}`;
        }
        else {
            className += ` dropzone`;
        }
        return className;
    }, [item, isDraggedOver]);

    const showChevron = React.useMemo((): boolean => {
        return item.canExpand
            && !item.isLoading
            && !(item.loaded && !hasChildren(item));
    }, [item]);

    React.useEffect(() => {
        let debounceTimer;
        if (isDraggedOver && showChevron && !item.expanded && !item.isLoading) {
            debounceTimer = setTimeout(() => {
                onExpandCollapseClick(item, false);
            }, 1500);
        }

        return () => {
            clearTimeout(debounceTimer);
        };
    }, [item, isDraggedOver, showChevron, onExpandCollapseClick]);

    const folderStyles: React.CSSProperties = isMobile ? mobileListItemStyle : {};

    return (
        <div
            ref={rootRef}
            className={className}
            style={folderStyles}
            onClick={(e) => {
                e.stopPropagation();
                onSiteClick(item);
            }}
            onDragEnter={dragEnter}
            onDragOver={dragOver}
            onDragLeave={dragLeave}>
            <div className={!allowScroll ? "list-item-link" : "list-item-link-scrollable"}>
                <div
                    style={treeViewItemStyle(item)}
                    onClick={(e) => {
                        if (showChevron) {
                            e.stopPropagation();
                            onExpandCollapseClick(item, false);
                        }
                    }}
                >
                    {item.isLoading && <Spinner size={SpinnerSize.xSmall} />}
                    {showChevron && <FontIcon iconName={item.expanded ? "ChevronDown" : "ChevronRight"} />}
                </div>
                {/* "Inactief" parity with SiteItem: an account Salesforce flags active === false
                    gets the same dimmed icon and pause badge as an inactive client-list site,
                    and stays clickable and expandable just like that site does. */}
                <div className="list-item-icon" style={item.isInactive ? { opacity: 0.4, filter: "grayscale(1)" } : undefined}>
                    <img src={BrandIcons.SharePoint20} height={20} width={20} />
                </div>
                <Text className="list-item-name">
                    <TooltipHost
                        content={`${item.description ?? item.name}${item.isHub ? ` (${languageStrings.HubSite})` : ""}`}
                        overflowMode={!(item.description && item.description.toLowerCase() !== item.name.toLowerCase()) ? TooltipOverflowMode.Parent : undefined}
                    >
                        {item.name}
                    </TooltipHost>
                </Text>
                {item.isInactive && <TooltipHost hostClassName="tess-account-inactive" content={languageStrings.InactiveClient} delay={TooltipDelay.long} styles={{ root: { display: "flex" } }}>
                    <PauseCircle20Regular style={smallPadLeft} />
                </TooltipHost>}
            </div>
            {item.canExpand && !item.isLoading && item.loaded && <TooltipHost content={languageStrings.Refresh}>
                <div className={isMobile ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
                    {/* <FontIcon
                        iconName="Refresh"
                        className="refresh-icon"
                        onClick={async (e) => {
                            e.stopPropagation();
                            onExpandCollapseClick(item, true);
                        }}
                    /> */}
                    <ArrowClockwise20Regular
                        onClick={(e) => {
                            e.stopPropagation();
                            onExpandCollapseClick(item, true);
                        }}
                    />
                </div>
            </TooltipHost>}
            <TooltipHost content={languageStrings.MoreActions}>
                <div className={isMobile ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
                    {/* <FontIcon
                        iconName="More"
                        onClick={(e) => {
                            e.stopPropagation();
                            onOptionsClick(item);
                        }}
                    /> */}
                    <MoreVertical20Regular
                        onClick={(e) => {
                            e.stopPropagation();
                            onOptionsClick(item);
                        }}
                    />
                </div>
            </TooltipHost>
        </div>
    )
};